import { DeveloperAppReview } from '../../../models/app';
import commonStyles from '../../../styles/commonStyles.module.scss';
import styles from './styles.module.scss';
import { StarRating } from '../../StarRating';
import { CommentBody } from './comment-body';
import React, { useState } from 'react';
import { OverFlowMenu, OverflowMenuOption } from '../../Common/OverFlowMenu/OverFlowMenu';
import { ReviewAppForm } from '../AppActions/ReviewApp/review-app-form';
import { DeleteReviewModal } from '../AppActions/delete-review-modal';
import { DeleteReviewResponse, FlagReviewResponse } from '../../../models/post-req-responses-payloads';
import { useTranslation } from 'react-i18next';
import { ReportReviewButton } from '../AppActions/report-review';
import { AppActionPostRequestResponseHandler, SubmitReviewRequestHandler } from '../AppActions/types';

interface ComponentProps {
  developerResponseEnabled: boolean;
  reviewData: DeveloperAppReview;
  reviewRemovedHandler: AppActionPostRequestResponseHandler<DeleteReviewResponse>;
  reportReviewHandler: AppActionPostRequestResponseHandler<FlagReviewResponse>;
  reviewSubmittedCallBack: SubmitReviewRequestHandler;
}

enum ComponentModes {
  Normal = 1,
  Edit,
  Delete,
}

enum OverFlowCommentItem {
  Edit,
  Delete,
  Report,
}

export const CommentBlock: React.FC<ComponentProps> = ({
  reviewData,
  developerResponseEnabled,
  reviewRemovedHandler,
  reportReviewHandler,
  reviewSubmittedCallBack,
}): Nullable<JSX.Element> => {
  const { t } = useTranslation();
  const { response, id, author, body, modifiedTime, title, starRating, developerApp } = reviewData;
  const { isSelf } = author;
  const { id: appId, name: appName } = developerApp;
  const [mode, setMode] = useState<ComponentModes>(ComponentModes.Normal);

  const removeReview: AppActionPostRequestResponseHandler<DeleteReviewResponse> = args => {
    setMode(ComponentModes.Normal);
    reviewRemovedHandler(args);
  };

  const reviewSubmitted: SubmitReviewRequestHandler = args => {
    const { reqResponse } = args;
    if (reqResponse && 'data' in reqResponse) {
      setMode(ComponentModes.Normal);
    }
    reviewSubmittedCallBack(args);
  };

  const editReview = (): void => {
    setMode(ComponentModes.Edit);
  };

  const toggleDeleteMode = (): void => {
    if (mode === ComponentModes.Delete) setMode(ComponentModes.Normal);
    else setMode(ComponentModes.Delete);
  };

  const OverFlowMenuItems: Record<OverFlowCommentItem, OverflowMenuOption> = {
    [OverFlowCommentItem.Edit]: {
      key: 'edit_review',
      renderItem: () => t('buttons.review_options.edit'),
      optionClickCallBack: editReview,
    },
    [OverFlowCommentItem.Delete]: {
      key: 'delete_review',
      renderItem: () => t('buttons.review_options.delete'),
      optionClickCallBack: toggleDeleteMode,
    },
    [OverFlowCommentItem.Report]: {
      key: 'report_review',
      renderItem: () => (
        <ReportReviewButton
          reviewId={reviewData.id}
          appId={appId}
          actionCallBack={reportReviewHandler}
          isMenuItem={true}
        />
      ),
      optionClickCallBack: undefined,
    },
  };

  const getOverFlowMenuOptions = (): OverflowMenuOption[] => {
    let options;
    if (isSelf) {
      options = [OverFlowMenuItems[OverFlowCommentItem.Edit], OverFlowMenuItems[OverFlowCommentItem.Delete]];
    } else {
      options = [OverFlowMenuItems[OverFlowCommentItem.Report]];
    }
    return options;
  };

  const overFlowMenuOptions = getOverFlowMenuOptions();

  return (
    <>
      {mode === ComponentModes.Edit ? (
        <ReviewAppForm
          appId={appId}
          appName={appName}
          reviewSubmittedCallBack={reviewSubmitted}
          cancelCallBack={() => setMode(ComponentModes.Normal)}
          review={reviewData}
        />
      ) : (
        <>
          <hr className={commonStyles.hr} />
          <div className={styles.block}>
            <div className={styles.review}>
              <div className={styles.title}>
                <p>
                  <strong>{title}</strong>
                </p>
              </div>
              <StarRating rating={starRating} size={'small'} />
              <CommentBody
                id={id}
                author={author.name}
                body={body}
                modifiedTime={modifiedTime}
                isSelf={isSelf}
              />
              {developerResponseEnabled && response && response.developerReference && (
                <CommentBody
                  id={id}
                  body={response.body}
                  modifiedTime={response.modifiedTime}
                  author={response.developerReference.name}
                  isResponse={true}
                />
              )}
            </div>
            <div className={styles.overFlowMenu}>
              <OverFlowMenu options={overFlowMenuOptions} />
            </div>
          </div>
          <DeleteReviewModal
            open={mode === ComponentModes.Delete}
            onClose={toggleDeleteMode}
            review={reviewData}
            editReviewHandler={editReview}
            appId={appId}
            deleteReviewHandler={removeReview}
          />
        </>
      )}
    </>
  );
};
